/**
 * [비즈니스 목적]
 * 경영정보 파일의 배치 AI 변환 실행 및 상태 관리를 담당하는 커스텀 훅
 *
 * [워크플로우]
 * 1. 파일 × 프롬프트 × 날짜 조합으로 배치 Job 생성
 * 2. 변환 방식에 따라 적절한 API 호출
 *    - Python 모드: 단일 배치 API로 모든 파일 처리 (브라우저 연결 제한 회피)
 *    - Claude 모드: 개별 Cloud Run Job 실행
 * 3. 실시간 진행률 업데이트 (batchProgress/batchTotal)
 * 4. Claude Fallback 상태 폴링 (5초 간격)
 * 5. 배치 실행 결과 폴링 (10초 간격)
 * 6. 검증 실패 사유 수집 및 표시
 *
 * [주요 기능]
 * 1. 배치 실행
 *    - executeBatch: 배치 변환 실행 메인 함수
 *    - Python 모드: /api/conversions/python-convert-batch (단일 또는 다중 날짜)
 *    - Claude 모드: /api/conversions/batch (개별 Cloud Run Job)
 *    - Execution Mode: python_only, python_gemini, full
 *
 * 2. 상태 폴링
 *    - startFallbackPolling: Claude Fallback 상태 모니터링 (5초 간격)
 *    - startBatchPolling: 배치 실행 결과 조회 (10초 간격)
 *    - pollJobStatus: Cloud Run Job 상태 추적 (Claude 모드)
 *
 * 3. 결과 관리
 *    - batchResults: 변환 결과 배열
 *    - validationErrors: Gemini 검증 실패 항목
 *    - fallbackStatus: Claude Fallback 등록 상태
 *
 * [반환 값]
 * - executing: 배치 실행 중 여부 (boolean)
 * - batchProgress: 완료된 작업 수 (number)
 * - batchTotal: 전체 작업 수 (number)
 * - batchResults: 변환 결과 배열 (array)
 * - error: 에러 메시지 (string|null)
 * - executeBatch: 배치 실행 함수
 * - setError: 에러 설정 함수
 * - cleanup: 폴링 정리 함수
 * - fallbackStatus: Fallback 상태 객체 { isActive, items, message }
 * - batchId: 배치 실행 ID (string|null)
 * - validationErrors: 검증 실패 항목 배열 (array)
 *
 * [executeBatch 파라미터]
 * - selectedTenant: 테넌트 코드 (string)
 * - filePromptMapping: 파일-프롬프트 매핑 객체 { fileId: [promptId, ...] }
 * - dateList: 기준일시 배열 (string[])
 * - selectedMode: 변환 모드 (python|text - legacy)
 * - conversionMode: 변환 방식 (claude|python)
 * - executionMode: 실행 단계 (python_only|python_gemini|full)
 * - files: 파일 정보 배열
 * - idToken: Google OAuth ID Token
 *
 * [변환 흐름 (Python 모드)]
 * - 단일 날짜: Python 변환 → Claude 검증 → 실패 시 Claude Fallback (Cloud Tasks)
 * - 다중 날짜: /api/conversions/batch-dates로 병렬 처리 (15분 타임아웃)
 *
 * [변환 흐름 (Claude 모드)]
 * - 개별 Cloud Run Job 실행 → 상태 폴링 (최대 30분) → 완료 시 결과 업데이트
 *
 * [성능 최적화]
 * - AbortController로 15분 타임아웃 설정
 * - 브라우저 도메인당 6개 연결 제한 회피 (단일 배치 API 사용)
 * - useCallback으로 폴링 함수 메모이제이션
 *
 * [사용 예시]
 * const { executing, batchProgress, batchTotal, executeBatch, cleanup } = useBatchExecution();
 *
 * useEffect(() => cleanup, [cleanup]); // 언마운트 시 폴링 정리
 *
 * const handleExecute = async () => {
 *   const result = await executeBatch({
 *     selectedTenant: tenantCode,
 *     filePromptMapping,
 *     dateList: ['20250101', '20250102'],
 *     conversionMode: 'python',
 *     executionMode: 'full',
 *     files,
 *     idToken
 *   });
 * };
 */

import { useState, useRef, useCallback } from 'react';
import apiService from '../../../services/api';

// Backend API URL
const BACKEND_API_URL = process.env.REACT_APP_API_BASE_URL !== undefined
  ? process.env.REACT_APP_API_BASE_URL
  : 'http://localhost:5000';

export const useBatchExecution = () => {
  const [executing, setExecuting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const [error, setError] = useState(null);
  const [pollingJobs, setPollingJobs] = useState([]); // Track jobs being polled

  // Claude Fallback 상태 추적
  const [fallbackStatus, setFallbackStatus] = useState({
    isActive: false,
    items: [],
    message: ''
  });
  const fallbackPollingRef = useRef(null);

  // 배치 상태 폴링 (검증 실패 사유 조회)
  const [batchId, setBatchId] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const batchPollingRef = useRef(null);

  /**
   * Claude Fallback 상태 폴링 시작 (제거됨 - 빈 함수로 유지)
   */
  const startFallbackPolling = useCallback((tenantCode, bsda) => {
    // Fallback 폴링 제거됨 - 아무 작업도 수행하지 않음
  }, []);

  /**
   * Claude Fallback 상태 폴링 중지 (제거됨 - 빈 함수로 유지)
   */
  const stopFallbackPolling = useCallback(() => {
    // Fallback 폴링 제거됨 - 아무 작업도 수행하지 않음
  }, []);

  // idToken을 저장하기 위한 ref
  const idTokenRef = useRef(null);

  /**
   * 배치 상태 폴링 시작 (검증 실패 사유 조회)
   * Cloud Tasks 등록 후 백그라운드에서 결과를 폴링
   */
  const startBatchPolling = useCallback((batchIdValue, idToken) => {
    if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current);
    }

    // idToken 저장
    if (idToken) {
      idTokenRef.current = idToken;
    }

    console.log(`🔍 배치 상태 폴링 시작: batch_id=${batchIdValue}`);
    setBatchId(batchIdValue);

    const poll = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json'
        };
        if (idTokenRef.current) {
          headers['Authorization'] = `Bearer ${idTokenRef.current}`;
        }

        const response = await fetch(`${BACKEND_API_URL}/api/conversions/batch-status/${batchIdValue}`, {
          credentials: 'include',
          headers
        });

        if (!response.ok) {
          console.error('배치 상태 조회 실패:', response.status);
          return;
        }

        const data = await response.json();
        console.log(`   배치 상태: ${data.status}, 성공: ${data.summary?.success_count || 0}, 실패: ${data.summary?.error_count || 0}`);

        // 검증 실패 사유 추출
        if (data.results && data.results.length > 0) {
          const errors = data.results
            .filter(r => r.gemini_validation_passed === false || r.error_message)
            .map(r => ({
              file_id: r.file_id,
              prompt_id: r.prompt_id,
              error: r.error_message || '검증 실패',
              validation_passed: r.gemini_validation_passed
            }));

          if (errors.length > 0) {
            setValidationErrors(errors);
          }

          // 결과 업데이트
          setBatchResults(prev => {
            // 기존 결과에 다운로드 URL 등 추가 정보 병합
            const updated = [...prev];
            for (const result of data.results) {
              const idx = updated.findIndex(r =>
                r.file_id === result.file_id && r.prompt_id === result.prompt_id
              );
              if (idx !== -1) {
                updated[idx] = {
                  ...updated[idx],
                  ...result,
                  status: result.status || 'success'
                };
              } else {
                updated.push({
                  ...result,
                  status: result.status || 'success'
                });
              }
            }
            return updated;
          });
        }

        // 완료되면 폴링 중지
        if (data.status === 'completed' || data.status === 'completed_with_errors') {
          console.log(`✅ 배치 처리 완료: ${data.status}`);
          stopBatchPolling();
        }
      } catch (err) {
        console.error('배치 상태 조회 실패:', err);
      }
    };

    // 즉시 한 번 실행 후 10초마다 폴링
    setTimeout(poll, 3000); // 3초 후 첫 조회 (Cloud Tasks 처리 시간 고려)
    batchPollingRef.current = setInterval(poll, 10000);
  }, []);

  /**
   * 배치 상태 폴링 중지
   */
  const stopBatchPolling = useCallback(() => {
    if (batchPollingRef.current) {
      console.log('🛑 배치 상태 폴링 중지');
      clearInterval(batchPollingRef.current);
      batchPollingRef.current = null;
    }
  }, []);

  /**
   * Poll Cloud Run Job status and update results when completed
   */
  const pollJobStatus = async (executionName, tenant_code, bsda, resultIndex) => {
    console.log(`🔄 Polling job status: ${executionName}`);

    const maxAttempts = 180; // Poll for up to 30 minutes (10s interval) - increased for Gemini validation cycles
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts++;

      try {
        const response = await apiService.getBatchExecutionStatus(executionName, tenant_code, bsda);

        if (!response.success) {
          console.error(`❌ Failed to get job status: ${response.error}`);
          clearInterval(pollInterval);
          setPollingJobs(prev => prev.filter(j => j !== executionName));
          return;
        }

        const jobData = response.data;
        console.log(`   Job status: ${jobData.status}, attempt ${attempts}/${maxAttempts}`);

        // Check if job is completed
        if (jobData.status === 'CONDITION_SUCCEEDED') {
          console.log(`✅ Job completed successfully: ${executionName}`);
          clearInterval(pollInterval);
          setPollingJobs(prev => prev.filter(j => j !== executionName));

          // Update the result with conversion_results
          if (jobData.conversion_results && jobData.conversion_results.length > 0) {
            setBatchResults(prev => {
              const updated = [...prev];
              // Find and update the result
              const index = updated.findIndex(r => r.execution_name === executionName);
              if (index !== -1) {
                updated[index] = {
                  ...updated[index],
                  status: 'success',
                  message: '변환이 완료되었습니다',
                  conversion_results: jobData.conversion_results,
                  completion_time: jobData.completion_time
                };
              }
              return updated;
            });
          }
        } else if (jobData.status === 'CONDITION_FAILED') {
          console.error(`❌ Job failed: ${executionName}`);
          clearInterval(pollInterval);
          setPollingJobs(prev => prev.filter(j => j !== executionName));

          // Update result with error
          setBatchResults(prev => {
            const updated = [...prev];
            const index = updated.findIndex(r => r.execution_name === executionName);
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                status: 'error',
                error: jobData.message || '변환 실패',
                completion_time: jobData.completion_time
              };
            }
            return updated;
          });
        } else if (attempts >= maxAttempts) {
          console.warn(`⏱️  Job polling timeout: ${executionName}`);
          clearInterval(pollInterval);
          setPollingJobs(prev => prev.filter(j => j !== executionName));
        }
      } catch (err) {
        console.error(`❌ Error polling job status:`, err);
        clearInterval(pollInterval);
        setPollingJobs(prev => prev.filter(j => j !== executionName));
      }
    }, 10000); // Poll every 10 seconds
  };

  /**
   * 배치 변환 실행
   * Python 모드: 단일 배치 API 호출로 모든 파일 처리 (브라우저 연결 제한 회피)
   * Claude 모드: 기존 방식 유지
   *
   * executionMode:
   * - python_only: Python 변환만
   * - python_gemini: Python 변환 + Gemini 검증
   * - full: Python 변환 + Gemini 검증 + Claude Fallback (기본값)
   */
  const executeBatch = async ({
    selectedTenant,
    filePromptMapping,  // { fileId: promptId } mapping
    dateList,
    selectedMode = 'python',  // Default to python (legacy)
    conversionMode = 'claude', // Default to claude
    executionMode = 'full',   // Execution mode: python_only, python_gemini, full
    files,
    idToken
  }) => {
    setExecuting(true);
    setError(null);
    setBatchResults([]);
    setBatchProgress(0);

    const selectedFileIds = Object.keys(filePromptMapping);

    try {
      // Create all job configurations
      const jobs = [];
      for (const fileId of selectedFileIds) {
        const fileInfo = files.find(f => f.file_id === fileId);
        const fileName = fileInfo?.displayName || fileId;
        const promptIds = filePromptMapping[fileId];  // 배열 또는 빈 배열

        console.log(`📋 Job 생성 - File: ${fileId}, promptIds:`, promptIds);

        for (const bsdaValue of dateList) {
          if (!promptIds || promptIds.length === 0) {
            // 프롬프트 미선택 → 모든 프롬프트 실행 (prompt_id 없이 전송)
            console.log(`   ➡️ Job 추가 (프롬프트 없음): ${fileId} @ ${bsdaValue}`);
            jobs.push({ fileId, fileName, bsdaValue, promptId: null });
          } else {
            // 선택된 프롬프트들에 대해 각각 job 생성
            for (const promptId of promptIds) {
              console.log(`   ➡️ Job 추가: ${fileId} @ ${bsdaValue} with prompt="${promptId}" (type: ${typeof promptId})`);
              jobs.push({ fileId, fileName, bsdaValue, promptId });
            }
          }
        }
      }

      const total = jobs.length;
      setBatchTotal(total);

      // Python 모드: 단일 배치 API 호출로 모든 파일 처리
      // 브라우저의 도메인당 6개 연결 제한(ERR_NETWORK_IO_SUSPENDED) 회피
      if (conversionMode === 'python') {
        console.log(`🚀 Python 배치 변환: ${total}개 job을 처리합니다.`);

        // Group jobs by bsda (same date jobs in one batch)
        const jobsByBsda = {};
        for (const job of jobs) {
          if (!jobsByBsda[job.bsdaValue]) {
            jobsByBsda[job.bsdaValue] = [];
          }
          jobsByBsda[job.bsdaValue].push({
            file_id: job.fileId,
            prompt_id: job.promptId || undefined
          });
        }

        const bsdaList = Object.keys(jobsByBsda);
        let completedCount = 0;
        const allResults = [];

        // 여러 날짜가 선택된 경우: batch-dates API로 병렬 처리
        if (bsdaList.length > 1) {
          console.log(`📤 다중 날짜 병렬 처리: ${bsdaList.length}개 날짜를 동시에 처리합니다.`);
          console.log(`   날짜 목록: ${bsdaList.join(', ')}`);

          const requestBody = {
            tenant_code: selectedTenant,
            bsda_list: bsdaList,
            execution_mode: 'python_only'  // 항상 python_only로 전달
          };

          try {
            // AbortController for 15-minute timeout (다중 날짜 병렬 처리 시 긴 응답 시간 고려)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);  // 15분

            const response = await fetch(`${BACKEND_API_URL}/api/conversions/batch-dates`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              credentials: 'include',  // 세션 쿠키 전송 (7일 인증 유지)
              body: JSON.stringify(requestBody),
              signal: controller.signal  // AbortController 연결
            });

            clearTimeout(timeoutId);  // 성공 시 타임아웃 취소

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || data.detail || '다중 날짜 병렬 변환 실패');
            }

            console.log(`✅ 다중 날짜 병렬 변환 완료: ${data.success}/${data.total_conversions} 성공`);

            // Process results from each date
            if (data.dates && Array.isArray(data.dates)) {
              for (const dateResult of data.dates) {
                const bsdaValue = dateResult.bsda;
                const dateResults = dateResult.results || [];

                for (const item of dateResults) {
                  const matchingJob = jobs.find(j => j.fileId === item.file_id && j.bsdaValue === bsdaValue);
                  allResults.push({
                    ...item,
                    fileName: matchingJob?.fileName || item.file_id,
                    bsda: bsdaValue,
                    promptId: item.prompt_id || '전체 프롬프트',
                    conversion_method: item.conversion_method || 'python',
                    output_path: item.output_path,
                    download_url: item.download_url,
                    // 검증 결과는 기본값 사용
                    gemini_validation_passed: item.gemini_validation_passed || false,
                    validation_errors: item.validation_errors || [],
                    fallback_used: false,
                    claude_fallback_registered: false,
                    requestBody
                  });
                }
              }
            }

            setBatchProgress(total);
            setBatchResults(allResults);

          } catch (err) {
            console.error(`❌ 다중 날짜 병렬 변환 실패:`, err);

            // Create error results for all jobs
            for (const [bsdaValue, batchJobs] of Object.entries(jobsByBsda)) {
              for (const job of batchJobs) {
                const matchingJob = jobs.find(j => j.fileId === job.file_id && j.bsdaValue === bsdaValue);
                allResults.push({
                  file_id: job.file_id,
                  fileName: matchingJob?.fileName || job.file_id,
                  bsda: bsdaValue,
                  promptId: job.prompt_id || '전체 프롬프트',
                  status: 'error',
                  error: err.message,
                  requestBody
                });
              }
            }

            setBatchProgress(total);
            setBatchResults(allResults);
          }

          return { success: true, results: allResults };
        }

        // 단일 날짜인 경우: 동기 API 사용 (Python 변환 + Gemini 검증)
        // Claude Fallback은 백엔드에서 Cloud Tasks로 처리됨
        for (const [bsdaValue, batchJobs] of Object.entries(jobsByBsda)) {
          console.log(`📤 배치 API 호출: BSDA=${bsdaValue}, ${batchJobs.length}개 job`);

          const requestBody = {
            tenant_code: selectedTenant,
            bsda: bsdaValue,
            jobs: batchJobs,
            execution_mode: 'python_only'  // 항상 python_only로 전달
          };

          try {
            // AbortController for 15-minute timeout (Claude Fallback 시 긴 응답 시간 고려)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);  // 15분

            const response = await fetch(`${BACKEND_API_URL}/api/conversions/python-convert-batch`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              credentials: 'include',  // 세션 쿠키 전송 (7일 인증 유지)
              body: JSON.stringify(requestBody),
              signal: controller.signal  // AbortController 연결
            });

            clearTimeout(timeoutId);  // 성공 시 타임아웃 취소

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || data.detail || 'Python 배치 변환 실패');
            }

            console.log(`✅ Python 배치 변환 완료: ${data.succeeded}/${data.total} 성공`);
            // 디버깅: 백엔드 응답 데이터 확인
            console.log('📦 Backend response data:', JSON.stringify(data, null, 2));

            // Process results
            const processedResults = [
              ...(data.results || []),
              ...(data.errors || [])
            ].map(item => {
              // Find matching job for fileName
              const matchingJob = jobs.find(j => j.fileId === item.file_id && j.bsdaValue === bsdaValue);

              // Claude Fallback이 Cloud Run Job으로 실행된 경우 → "완료" 상태로 처리
              // (백그라운드에서 Claude Job이 처리하므로 사용자 관점에서는 완료)
              let itemStatus = item.status;
              if (item.claude_fallback_registered) {
                itemStatus = 'claude_fallback_queued';  // Cloud Run Job 실행됨
              }

              return {
                ...item,
                fileName: matchingJob?.fileName || item.file_id,
                bsda: bsdaValue,
                promptId: item.prompt_id || '전체 프롬프트',
                conversion_method: item.conversion_method || 'python',
                output_path: item.output_path,
                download_url: item.download_url,
                bigquery: item.bigquery,
                status: itemStatus,
                // 검증 결과는 기본값 사용
                gemini_validation_passed: item.gemini_validation_passed || false,
                validation_errors: item.validation_errors || [],
                fallback_used: false,
                claude_fallback_registered: false,
                requestBody
              };
            });

            // Update progress and results
            completedCount += batchJobs.length;
            setBatchProgress(completedCount);
            setBatchResults(prev => [...prev, ...processedResults]);
            allResults.push(...processedResults);

          } catch (err) {
            console.error(`❌ Python 배치 변환 실패 (BSDA=${bsdaValue}):`, err);

            // Create error results for all jobs in this batch
            const errorResults = batchJobs.map(job => {
              const matchingJob = jobs.find(j => j.fileId === job.file_id && j.bsdaValue === bsdaValue);
              return {
                file_id: job.file_id,
                fileName: matchingJob?.fileName || job.file_id,
                bsda: bsdaValue,
                promptId: job.prompt_id || '전체 프롬프트',
                status: 'error',
                error: err.message,
                requestBody
              };
            });

            completedCount += batchJobs.length;
            setBatchProgress(completedCount);
            setBatchResults(prev => [...prev, ...errorResults]);
            allResults.push(...errorResults);
          }
        }

        return { success: true, results: allResults };

      } else {
        // Claude 모드: 기존 방식 유지 (개별 요청)
        console.log(`🚀 Claude 모드: ${total}개의 Cloud Run Job을 병렬로 실행합니다.`);

        let completedCount = 0;
        const promises = jobs.map(async ({ fileId, fileName, bsdaValue, promptId }) => {
          let requestBody = {
            tenant_code: selectedTenant,
            file_id: fileId,
            bsda: bsdaValue
          };

          try {
            console.log(`🚀 Cloud Run Job 시작: ${fileName} @ ${bsdaValue} (프롬프트: ${promptId || '모든 프롬프트'})`);

            const url = `${BACKEND_API_URL}/api/conversions/batch`;

            requestBody = {
              tenant_code: selectedTenant,
              file_id: fileId,
              bsda: bsdaValue
            };

            if (promptId) {
              requestBody.prompt_id = promptId;
            }

            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              credentials: 'include',  // 세션 쿠키 전송 (7일 인증 유지)
              body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || data.detail || 'Cloud Run Job 실행 실패');
            }

            console.log(`✅ Claude 변환 시작: ${fileName} @ ${bsdaValue}`);

            const result = {
              fileId,
              fileName,
              bsda: bsdaValue,
              promptId: promptId || '전체 프롬프트',
              status: 'started',
              execution_name: data.execution_name,
              logs_url: data.logs_url,
              message: 'Cloud Run Job이 시작되었습니다. 변환 완료 대기 중...',
              conversion_method: 'agent',
              requestBody
            };

            completedCount++;
            setBatchProgress(completedCount);
            setBatchResults(prev => [...prev, result]);

            setPollingJobs(prev => [...prev, data.execution_name]);
            pollJobStatus(data.execution_name, selectedTenant, bsdaValue);

            return result;

          } catch (err) {
            console.error(`❌ Cloud Run Job 실행 실패 (${fileName} @ ${bsdaValue}):`, err);

            const errorResult = {
              fileId,
              fileName,
              bsda: bsdaValue,
              promptId: promptId || '전체 프롬프트',
              status: 'error',
              error: err.message,
              requestBody
            };

            completedCount++;
            setBatchProgress(completedCount);
            setBatchResults(prev => [...prev, errorResult]);

            return errorResult;
          }
        });

        const allResults = await Promise.all(promises);
        return { success: true, results: allResults };
      }

    } catch (err) {
      console.error('❌ 배치 변환 중 오류:', err);
      setError(err.message || '배치 변환 중 오류가 발생했습니다');
      return { success: false, error: err.message };
    } finally {
      setExecuting(false);
      // Fallback 폴링 제거됨
    }
  };

  /**
   * 배치 폴링 수동 중지 (컴포넌트 언마운트 시 호출)
   */
  const cleanup = useCallback(() => {
    stopFallbackPolling();
    stopBatchPolling();
  }, [stopFallbackPolling, stopBatchPolling]);

  return {
    executing,
    batchProgress,
    batchTotal,
    batchResults,
    error,
    executeBatch,
    setError,
    cleanup,
    // Claude Fallback 상태
    fallbackStatus,
    // 배치 상태 (검증 실패 사유)
    batchId,
    validationErrors
  };
};
